'use client';

import { memo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import { nameToGradient } from '@/lib/avatar-gradient';
import { formatCategory } from '@/lib/utils';
import type { StoreFeedItem } from '@/lib/api';
import { Package, Store } from 'lucide-react';
import { InstagramProductPhoto } from '@/components/instagram/instagram-product-photo';
import { DisplayStars } from '@/components/ui/star-rating';

interface StoreCardProps {
  store: StoreFeedItem;
  onClick?: (slug: string) => void;
  /** Own-store card: yellow border + top-right "My Store" chip. */
  isOwnStore?: boolean;
  priority?: boolean;
}

function coverUrlWithCacheBuster(url: string | undefined, isOwnStore: boolean): string | undefined {
  if (!url) return url;
  if (!isOwnStore || typeof window === 'undefined') return url;
  const v = window.sessionStorage.getItem('sweetspot_cover_updated');
  if (!v) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${v}`;
}

export const StoreCard = memo(function StoreCard({ store, onClick, isOwnStore, priority = false }: StoreCardProps) {
  const [imgError, setImgError] = useState(false);
  const baseUrl = store.coverImageUrl ?? store.avatarUrl ?? undefined;
  const heroUrl = coverUrlWithCacheBuster(baseUrl, !!isOwnStore) ?? baseUrl;
  const unclaimed = store.claimStatus === 'unclaimed';

  const href = store.slug ? `/s/${store.slug}` : '#';

  const open = () => { if (store.slug && onClick) onClick(store.slug); };
  return (
    <div
      className={`group relative rounded-xl border bg-card transition-all duration-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] active:duration-100 cursor-pointer block ${isOwnStore ? 'border-2 border-amber-400' : ''}`}
    >
      {/* Cover / Avatar */}
      <Link href={href} onClick={open} prefetch={false} className="relative block aspect-[16/10] bg-muted overflow-hidden">
        {heroUrl && !imgError ? (
          <Image
            src={heroUrl}
            alt={store.name}
            fill
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            className={`object-cover transition-transform duration-300 group-hover:scale-105 ${unclaimed ? 'opacity-40' : ''}`}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${nameToGradient(store.name)} ${unclaimed ? 'opacity-40' : ''}`}>
            <span className="text-2xl font-bold text-white/80 sm:text-4xl">
              {store.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* My Store chip (same style as tab button). */}
        {isOwnStore && (
          <div className="absolute top-2 right-2">
            <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-[10px] font-medium text-foreground shadow-sm sm:gap-1.5 sm:px-3 sm:py-1 sm:text-xs">
              <Store className="h-3 w-3" />
              My Store
            </span>
          </div>
        )}

        {/* Unclaimed chip */}
        {unclaimed && !isOwnStore && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-muted-foreground text-[10px] border-0">
              Unclaimed
            </Badge>
          </div>
        )}

        {/* Product count */}
        <div className="absolute bottom-2 right-2 z-10">
          <span className="inline-flex items-center gap-1 rounded-md bg-background/90 backdrop-blur-sm px-1.5 py-0.5 text-[10px] font-medium sm:px-2 sm:text-xs">
            <Package className="h-3 w-3" />
            {store.productCount}
          </span>
        </div>

        {store.category && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="bg-primary/90 text-primary-foreground text-[9px] sm:text-[10px] backdrop-blur-sm border-0">
              {formatCategory(store.category)}
            </Badge>
          </div>
        )}
      </Link>

      {/* Content */}
      <Link href={href} onClick={open} prefetch={false} className="block p-2.5 space-y-1.5 sm:p-3 sm:space-y-2">
        <div className="flex items-center gap-2">
          {store.avatarUrl ? (
            <div className="relative w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden shrink-0">
              <Image
                src={store.avatarUrl}
                alt=""
                fill
                sizes="24px"
                className="object-cover"
              />
            </div>
          ) : (
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br ${nameToGradient(store.name)} flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white shrink-0`}>
              {store.name.charAt(0).toUpperCase()}
            </div>
          )}
          <h2 className="font-semibold text-[13px] sm:text-sm leading-tight truncate">{store.name}</h2>
        </div>

        {(store.avgRating != null && store.ratingCount >= 3) && (
          <div className="flex items-center gap-1">
            <DisplayStars score={store.avgRating} size="xs" />
            <span className="text-[10px] text-muted-foreground">
              {store.avgRating.toFixed(1)} ({store.ratingCount})
            </span>
          </div>
        )}


        {(store.avgRating != null && store.ratingCount > 0 && store.ratingCount < 3) && (
          <p className="text-[10px] text-muted-foreground">New · {store.ratingCount} rating{store.ratingCount > 1 ? 's' : ''}</p>
        )}
        {store.bio && (
          <p className="hidden sm:block text-xs text-muted-foreground line-clamp-2">{store.bio}</p>
        )}

        {(store.locationNeighborhood || store.locationCity) && (
          <p className="text-[10px] sm:text-[11px] text-muted-foreground truncate">
            {(() => { const n=store.locationNeighborhood?.trim(); const c=store.locationCity?.trim(); if (n && c && n.toLowerCase()===c.toLowerCase()) return c; return [n||null,c||null].filter(Boolean).join(', '); })()}
          </p>
        )}

      </Link>
      {/* Preview thumbnails */}
        {store.previewProducts.length > 0 && (
          <div className="flex flex-wrap items-start gap-1.5 px-2.5 pb-2.5 sm:px-3 sm:pb-3">
            {store.previewProducts.slice(0, 4).map((p, i) => (
              <InstagramProductPhoto key={i} photo={p.instagramPhoto} title={p.title} imageHref={href} onImageClick={open}
                compactSource className={`w-11 shrink-0 overflow-hidden rounded-md bg-muted ${i === 3 ? 'hidden sm:block' : ''}`}
                imageClassName="h-11 w-11 object-cover">
                <div className="relative h-11 w-11">
                  {p.imageUrl ? (
                    <Image
                      src={p.imageUrl}
                      alt={p.title}
                      fill
                      sizes="44px"
                      className="object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Package className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
              </InstagramProductPhoto>
            ))}
          </div>
        )}
    </div>
  );
});
